import React, { useCallback, useContext, useMemo, useState } from 'react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import FileInputButton from '../../../../components/admin/FileInputButton'
import RequiredText from '../../../default/form/RequiredText'
import useGDK from '../../../../providers/admin/gdk/useGDK'
import useT from '../../../../i18ns/admin/useT'
import { useCommonStyles } from '../../../../utils/admin/StyleHook'
import useGlobalDialog from '../../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../../utils/default/DialogHelper'
import { useGetDataByPayload } from '../../../../utils/default/ComplexFlowHook'
import { ActivityWalletCsvRes, ActivityWalletUserRes } from '@golden/gdk-admin'
import { defaultValue, FormContext } from '../../../../views/admin/activity/ActivityWalletCreatePage'
import { PageFlowType } from '../../../../utils/default/PageFlowHook'
import { getCsvTemplate } from '../../../../utils/default/FormHelper'

const TextField = React.memo(MuiTextField)

const Step1: React.FC<{ setResCsv: (value: ActivityWalletCsvRes | null) => void, setResUser: (value: ActivityWalletUserRes | null) => void, pageFlow: PageFlowType }> = React.memo(({ setResUser, setResCsv, pageFlow }) => {
  const { t } = useT()
  const { value: form, dispatch } = useContext(FormContext)
  const commonClasses = useCommonStyles()
  const gdk = useGDK()
  const globalDialog = useGlobalDialog()

  const [account, setAccount] = useState('')
  const payloadFile = useMemo(() => ({ file: form.file as File }), [form.file])
  const payloadAccount = useMemo(() => ({ account: form.account }), [form.account])

  const csvTemplate = useMemo(() => {
    const title = [t('common.playerAccount'), t('common.cash'), `${t('common.activityContent')}(${t('common.stringMaximum', { amount: 255 })})`, t('common.exchangedDepositOrderNo')].join(',')
    return getCsvTemplate(title)
  }, [t])

  useGetDataByPayload({
    payload: payloadAccount,
    gdkFunc: (payload) => gdk.activity.getActivityWalletUserInfo(payload),
    gdkFuncDependencies: [gdk],
    canLoadData: !!form.account,
    onBeforeFetch: () => {
      setResUser(null)
      setResCsv(null)
      pageFlow.setLoadingStart()
    },
    onSuccess: (resUser: { id: number, account: string, is_not_activity: boolean }) => {
      setResUser(resUser)
      pageFlow.setContentShow()
    },
    onError: (error) => {
      setAccount('')
      dispatch({ type: 'change', label: 'account', value: '' })
      pageFlow.setError(error.message)
    }
  })

  useGetDataByPayload({
    payload: payloadFile,
    gdkFunc: (payload) => gdk.activity.postActivityWalletUserInfoCsv(payload),
    gdkFuncDependencies: [gdk],
    canLoadData: !!form.file,
    onBeforeFetch: () => {
      setAccount('')
      setResUser(null)
      setResCsv(null)
      pageFlow.setLoadingStart()
    },
    onSuccess: (resCsv: ActivityWalletCsvRes) => {
      setResCsv(resCsv)
      pageFlow.setContentShow()
      globalDialog.setConfig(createGlobalDialogConfig({
        showIcon: true,
        variant: 'success',
        showCancel: false,
        message: t('dialog.createActivityWalletCsv', { account: resCsv.account_duplicated, cash: resCsv.warning_amount })
      }))
      globalDialog.setOpen({
        id: 'validateActivityWalletCsvSuccess',
        value: true,
        isOK: false
      })
    },
    onError: (error) => {
      setResCsv(null)
      globalDialog.setConfig(createGlobalDialogConfig({
        showIcon: true,
        variant: 'error',
        showCancel: false,
        message: error.message
      }))
      globalDialog.setOpen({
        id: 'validateActivityWalletCsvFailed',
        value: true,
        isOK: false
      })
      dispatch({ type: 'change', label: 'file', value: null })
      pageFlow.setContentShow()
    }
  })

  const uploadFile = useCallback((file: File) => {
    const value = {
      ...defaultValue,
      file
    }
    dispatch({
      type: 'set',
      value
    })
  }, [])

  const search = useCallback(() => {
    const value = {
      ...defaultValue,
      account
    }
    dispatch({
      type: 'set',
      value
    })
  }, [account])
  return (
    <>
      <Grid item xs={12}>
        <Box
          paddingY={1.25}
          paddingX={2}
          className={commonClasses.pinkTitleBar}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
              <Typography variant="h5">
                {t('common.playerSearch')}
              </Typography>
            </Grid>
            <Grid>
              <a
                href={csvTemplate}
                download={`${t('common.template')}.csv`}
                className={commonClasses.anchor}
              >
                {t('common.downloadCsvTemplate')}
              </a>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between">
          <Box width={300}>
            <TextField
              label={`${t('common.playerAccount')} *`}
              value={account} onChange={(e) => { setAccount(e.target.value) }}
              fullWidth
            />
            <Box paddingTop={2}>
              <RequiredText />
            </Box>
          </Box>
          <FileInputButton onChange={uploadFile} loading={false} />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end">
          <Button className={commonClasses.purpleGradualButton} onClick={search} disabled={account === ''}>
            {t('common.search')}
          </Button>
        </Box>
      </Grid>
    </>
  )
})

export default React.memo(Step1)